/* eslint-disable no-unused-vars */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { listEvaluations, listEvalRuns, getEvalRun, createEvalRun, listEvaluators } from "../src/services/evaluationsApi.js";
import { formatDateTimeInTz } from "../src/utils/index.js";
import { Card, CardContent, CardHeader, CardTitle } from "../src/components/ui/card.jsx";
import { Button } from "../src/components/ui/button.jsx";
import { Badge } from "../src/components/ui/badge.jsx";
import { Checkbox } from "../src/components/ui/checkbox.jsx";
import { Input } from "../src/components/ui/input.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../src/components/ui/tabs.jsx";
import { Select } from "../src/components/ui/select.jsx";
import { useToast } from "../src/components/ui/toastContext.js";
import { TestTube, Play, Clock, CheckCircle, XCircle, ListFilter, Loader2, Info } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { formatDate } from "../src/utils/index.js";
import { getEvaluatorDescription, getEvaluatorCategory } from "../src/constants/evaluatorDescriptions.js";

function useLocalRunHistory() {
  const key = "evaluation_runs";
  const [runs, setRuns] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(runs));
    } catch (e) {
      // ignore localStorage persistence errors (private mode, quota, etc.)
    }
  }, [runs]);

  return [runs, setRuns];
}

export default function EvaluationRuns() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [matchingCases, setMatchingCases] = useState([]);
  const [runs, setRuns] = useLocalRunHistory();

  // Filters
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "");
  const [authFilter, setAuthFilter] = useState(searchParams.get("auth") || "all");
  // Removed legacy status filter (draft/ready/submitted)
  const [presetFilter, setPresetFilter] = useState(searchParams.get("preset") || "all");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");

  // Collections integration
  const [remoteCollections, setRemoteCollections] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [orgIdForCollections, setOrgIdForCollections] = useState("");
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [activeView, setActiveView] = useState(null); // { id, name, kind: 'selection' | 'criteria' }
  const [selectionIds, setSelectionIds] = useState(null); // array of ids when a selection-based view is applied

  // Run options
  const environment = "dev"; // ← fixed to dev only
  const [title, setTitle] = useState("");
  const [concurrency, setConcurrency] = useState(5);
  // NEW: evaluator discovery and choices
  const [availableEvaluators, setAvailableEvaluators] = useState({});
  const [useRag, setUseRag] = useState(true);
  const [selectedEvaluators, setSelectedEvaluators] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState("");

  // Persist key UI settings across reloads
  useEffect(() => {
    try {
      const raw = localStorage.getItem('evalruns_settings');
      if (raw) {
        const s = JSON.parse(raw);
        if (typeof s.useRag === 'boolean') setUseRag(s.useRag);
        if (Array.isArray(s.selectedEvaluators)) setSelectedEvaluators(s.selectedEvaluators);
        if (Number.isFinite(s.concurrency)) setConcurrency(Math.max(1, Math.min(8, Number(s.concurrency))));
      }
      const tRaw = localStorage.getItem('evalruns_templates');
      if (tRaw) {
        const list = JSON.parse(tRaw);
        if (Array.isArray(list)) setTemplates(list);
      }
    } catch (e) {
      // ignore
    }
  }, []);
  useEffect(() => {
    try {
      const s = { useRag, selectedEvaluators, concurrency };
      localStorage.setItem('evalruns_settings', JSON.stringify(s));
      localStorage.setItem('evalruns_templates', JSON.stringify(templates));
    } catch (e) {
      // ignore
    }
  }, [useRag, selectedEvaluators, concurrency, templates]);

  // Confirmation pop-up removed in favor of non-blocking toast

  useEffect(() => {
    const params = {};
    const q = searchQuery.trim();
    if (q) params.q = q;
    if (stateFilter) params.state = stateFilter;
    if (authFilter !== "all") params.auth = authFilter;
    // status filter removed
    if (presetFilter !== "all") params.preset = presetFilter;
    setSearchParams(params);
  }, [stateFilter, authFilter, presetFilter, searchQuery, setSearchParams]);

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      let cases = [];
      if (selectionIds && Array.isArray(selectionIds) && selectionIds.length) {
        // Pull a wide slice without filters, then narrow to explicit IDs so preview == Saved collection
        const wideLimit = Math.max(1000, selectionIds.length);
        cases = await listEvaluations({ limit: wideLimit });
      } else {
        // Normal filtered fetch
        cases = await listEvaluations({
          state: stateFilter || undefined,
          auth: authFilter !== "all" ? authFilter : undefined,
          preset: presetFilter !== "all" ? presetFilter : undefined,
          // status filter removed
          limit: 500,
        });
      }

      let filtered = cases;
      if (selectionIds && Array.isArray(selectionIds) && selectionIds.length) {
        const idSet = new Set(selectionIds);
        filtered = cases.filter((tc) => idSet.has(tc.id));
      } else {
        const q = searchQuery.trim().toLowerCase();
        filtered = q
          ? cases.filter((tc) =>
              [tc.title, tc.user_input, tc?.config_data?.project?.council_name, tc?.config_data?.project?.address]
                .map((v) => (typeof v === "string" ? v.toLowerCase() : ""))
                .some((s) => s.includes(q))
            )
          : cases;
      }

      setMatchingCases(filtered);
    } catch (e) {
      console.error("Failed to load preview", e);
      setMatchingCases([]);
    }
    setPreviewLoading(false);
  };

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NEW: fetch evaluator registry for multi-select
  useEffect(() => {
    (async () => {
      try {
        const reg = await listEvaluators();
  setAvailableEvaluators(reg || {});
  setSelectedEvaluators((prev) => (prev && prev.length ? prev : Object.keys(reg || {})));
      } catch (e) {
        console.error('Failed to fetch evaluators', e);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(loadPreview, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, authFilter, presetFilter, searchQuery, selectionIds, activeView]);

  // Infer dominant org_id from current preview and fetch shared collections
  useEffect(() => {
    const counts = {};
    for (const tc of matchingCases) {
      const oid = tc?.config_data?.org_id;
      if (oid) counts[oid] = (counts[oid] || 0) + 1;
    }
    const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
    if (dominant && dominant !== orgIdForCollections) {
      setOrgIdForCollections(dominant);
    }
  }, [matchingCases, orgIdForCollections]);

  useEffect(() => {
    const fetchRemoteCollections = async (orgId) => {
      try {
        setRemoteLoading(true);
        const qs = new URLSearchParams({ scope: "org", org_id: orgId });
        const res = await fetch(`/api/collections?${qs.toString()}`);
        if (!res.ok) throw new Error(`List failed: ${res.status}`);
        const json = await res.json();
        const items = Array.isArray(json?.items) ? json.items : [];
        setRemoteCollections(items);
      } catch (e) {
        console.error("Failed to load shared collections", e);
        setRemoteCollections([]);
      } finally {
        setRemoteLoading(false);
      }
    };
    if (orgIdForCollections) fetchRemoteCollections(orgIdForCollections);
  }, [orgIdForCollections]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setStateFilter("");
    setAuthFilter("all");
    setPresetFilter("all");
    // Do not clear selection collection here; use the Clear collection button for that
  };

  const applySelectedCollection = () => {
    if (!selectedCollectionId) return;
    const col = remoteCollections.find((c) => c.id === selectedCollectionId);
    if (!col) return;
    const crit = col.criteria || {};
    if (Array.isArray(crit.ids) && crit.ids.length) {
      // Selection collection: preview now shows EXACT IDs
      setSelectionIds(crit.ids);
      setActiveView({ id: col.id, name: col.name, kind: "selection" });
      return;
    }
    // Criteria collection: update filters so preview matches the collection; clear any selection-id filter
    setStateFilter(crit.stateFilter || "");
    setAuthFilter(crit.authFilter || "all");
    setPresetFilter(crit.presetFilter || "all");
    setSearchQuery(crit.searchQuery || "");
    setSelectionIds(null);
    setActiveView({ id: col.id, name: col.name, kind: "criteria" });
  };

  const handleStartRun = async () => {
    setLoading(true);
    try {
      // Always submit the exact preview set (fidelity with UI),
      // preferring explicit selection IDs when present.
      const idsForRun = (Array.isArray(selectionIds) && selectionIds.length)
        ? selectionIds
        : (Array.isArray(matchingCases) ? matchingCases.map(tc => tc.id) : []);

      if (!idsForRun.length) {
        throw new Error('No test cases to run. Adjust filters or apply a collection.');
      }

      // Send filters for traceability; backend will strictly use ids.
      const filters = {
        state: stateFilter || undefined,
        auth: authFilter !== "all" ? authFilter : undefined,
        // status filter removed
        preset: presetFilter !== "all" ? presetFilter : undefined,
        limit: Math.max(idsForRun.length, 500), // harmless; ids take precedence
      };

      const options = {
        environment,
        concurrency,
        notes: title.trim() || undefined,
        ids: idsForRun,
        // NEW: forward options
        use_rag: useRag,
        evaluators: selectedEvaluators,
        // Store collection ID for later filtering/mapping - use activeView.id when available
        collection_id: activeView?.id || selectedCollectionId || undefined,
      };

      console.log('🚀 Creating run with options:', { 
        ...options, 
        selectedCollectionId,
        activeView: activeView?.name,
        collectionFilterDebug: { 
          selectedCollectionId, 
          activeViewId: activeView?.id,
          finalCollectionId: activeView?.id || selectedCollectionId || undefined,
          hasActiveView: !!activeView 
        }
      });

      const result = await createEvalRun(filters, options);

      // Store locally for quick access
      setRuns((prev) => [
        {
          id: result.id,
          created_at: new Date().toISOString(),
          status: result.status,
          environment,
          concurrency,
          notes: title.trim() || undefined,
          use_rag: useRag,
          evaluators: selectedEvaluators,
          collection_id: activeView?.id || selectedCollectionId || undefined,
          counts: { selected: idsForRun.length },
        },
        ...prev,
      ]);

      // Non-blocking toast and auto-navigate to results
      const selCount = selectedEvaluators.length;
      addToast({
        variant: 'success',
        title: 'Run queued',
        message: `${useRag ? 'RAG ON' : 'RAG OFF'}, ${selCount} evaluator${selCount === 1 ? '' : 's'}, concurrency ${concurrency}`,
        durationMs: 3500,
      });
      setTimeout(() => {
        try {
          navigate('/EvaluationResults');
        } catch {
          window.location.href = '/EvaluationResults';
        }
      }, 800);
    } catch (e) {
      console.error('Failed to start run', e);
      alert(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "queued":
        return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">Queued</Badge>;
      case "running":
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">Running</Badge>;
      case "success":
        return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">Success</Badge>;
      case "failed":
        return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const runButtonDisabled =
    loading ||
    previewLoading ||
    ((matchingCases.length === 0) && !(activeView?.kind === 'selection' && selectionIds?.length)) ||
    // If a registry exists, require at least one evaluator selected
    ((Object.keys(availableEvaluators || {}).length > 0) && selectedEvaluators.length === 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">Run Evaluations</h1>
            <p className="text-slate-600 dark:text-slate-400">Select test cases, configure options, and start a run. Integration with your runner will be wired next.</p>
          </div>
        </div>

        <Tabs defaultValue="run" className="w-full">
          <TabsList data-tour="evalruns-tabs">
            <TabsTrigger value="run" data-tour="evalruns-tab-run">Run</TabsTrigger>
            <TabsTrigger value="history" data-tour="evalruns-tab-history">History</TabsTrigger>
          </TabsList>

        <TabsContent value="run" className="mt-4">
            <div className="grid gap-6">
              <Card className="shadow-sm border dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg dark:text-slate-100">Filters</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid gap-3 md:gap-4 md:grid-cols-12 md:items-center">
                    <div className="flex-1 md:col-span-8">
                      <Input
                        placeholder="Search title, input, council, address..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full"
                        data-tour="evalruns-search"
                      />
                    </div>
                    <div className="flex items-center gap-2 md:col-span-4 md:justify-end">
                      <label className="text-xs text-slate-600 dark:text-slate-400">Saved collection:</label>
                      <select
                        className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                        value={selectedCollectionId}
                        onChange={(e) => setSelectedCollectionId(e.target.value)}
                        disabled={remoteLoading || remoteCollections.length === 0}
                        title={remoteLoading ? 'Loading shared collections…' : 'Choose a shared collection'}
                        data-tour="evalruns-collections"
                      >
                        <option value="">{remoteLoading ? 'Loading…' : 'Select a collection…'}</option>
                        {remoteCollections.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}{Array.isArray(c?.criteria?.ids) ? ' (selection)' : ''}
                          </option>
                        ))}
                      </select>
                      <Button
                        className="px-3 py-1.5 text-xs"
                        variant="outline"
                        onClick={applySelectedCollection}
                        disabled={!selectedCollectionId}
                        title="Apply the selected collection to this page"
                      >
                        Apply
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:col-span-12" data-tour="evalruns-state-filters">
                      {["qa", "planning_pathway", "document_checklist", "application"].map((stateKey) => (
                        <button
                          key={stateKey}
                          className={`px-3 py-1.5 text-xs rounded border ${stateFilter === stateKey ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                          onClick={() => setStateFilter(stateKey)}
                          title={`Filter by ${stateKey.replace("_", " ")}`}
                        >
                          {stateKey.replace("_", " ")}
                        </button>
                      ))}
                      <button
                        className={`px-3 py-1.5 text-xs rounded border ${stateFilter === "" ? "bg-slate-50 border-slate-300 text-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                        onClick={() => setStateFilter("")}
                        title="Show all states"
                      >
                        All states
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:col-span-12" data-tour="evalruns-auth-filters">
                      <button
                        className={`px-3 py-1.5 text-xs rounded border ${authFilter === "anonymous" ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                        onClick={() => setAuthFilter("anonymous")}
                        title="Anonymous"
                      >
                        Anonymous
                      </button>
                      <button
                        className={`px-3 py-1.5 text-xs rounded border ${authFilter === "signed_in" ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                        onClick={() => setAuthFilter("signed_in")}
                        title="Logged in"
                      >
                        Logged in
                      </button>
                      <button
                        className={`px-3 py-1.5 text-xs rounded border ${authFilter === "all" ? "bg-slate-50 border-slate-300 text-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                        onClick={() => setAuthFilter("all")}
                        title="Show all auth statuses"
                      >
                        All auth
                      </button>
                    </div>
                    {activeView && (
                      <div className="flex items-center gap-2 md:col-span-12">
                        <span className="text-xs rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200" title="Active collection">
                          Using: {activeView.name}{activeView.kind === 'selection' ? ' (selection)' : ''}
                        </span>
                        <Button
                          className="px-2 py-1 text-xs"
                          variant="ghost"
                          onClick={() => { setActiveView(null); setSelectedCollectionId(""); setSelectionIds(null); }}
                          title="Clear applied collection"
                        >
                          Clear collection
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="shadow-lg border dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 backdrop-blur-sm md:col-span-2">
                  <CardHeader className="border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg dark:text-slate-100">Selection Preview</CardTitle>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {previewLoading ? (
                          <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</span>
                        ) : (
                          <span>{matchingCases.length} test cases</span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    {activeView?.kind === 'selection' && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Showing the exact Saved collection selection (by IDs). Other filters are ignored while this collection is applied.
                      </div>
                    )}
                    {matchingCases.length === 0 ? (
                      <div className="text-sm text-slate-600 dark:text-slate-300">No test cases match your filters.</div>
                    ) : (
                      <div className="space-y-3">
                        {matchingCases.slice(0, 10).map((tc) => (
                          <div key={tc.id} className="p-3 rounded-lg border border-slate-200/70 dark:border-slate-700 bg-white dark:bg-slate-900/60">
                            <div className="flex items-center justify-between">
                              <div className="font-semibold text-slate-800 dark:text-slate-100 truncate mr-3">{tc.title || tc.user_input?.slice(0, 64) || tc.id}</div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-slate-600 dark:text-slate-300">{tc.preset_type?.replace("_", " ") || "custom"}</Badge>
                                <Badge variant="outline" className="text-slate-600 dark:text-slate-300">{tc.config_data?.state || "N/A"}</Badge>
                                {/* status removed */}
                              </div>
                            </div>
                            {tc.user_input && (
                              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">{tc.user_input}</div>
                            )}
                          </div>
                        ))}
                        {matchingCases.length > 10 && (
                          <div className="text-xs text-slate-500 dark:text-slate-400">…and {matchingCases.length - 10} more</div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="shadow-lg border dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 backdrop-blur-sm" data-tour="evalruns-options">
                  <CardHeader className="border-b border-slate-100 dark:border-slate-800">
                    <CardTitle className="text-lg dark:text-slate-100">Run Options</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Title</label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional title for this run" data-tour="evalruns-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Environment</label>
                        <div className="text-sm border rounded px-2 py-2 bg-slate-50 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700 w-full">dev</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Concurrency</label>
                        <select className="text-sm border rounded px-2 py-2 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700 w-full" value={concurrency} onChange={(e) => setConcurrency(Math.max(1, Math.min(8, Number(e.target.value))))}>
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Max 8 concurrent to avoid rate limits.</div>
                      </div>
                    </div>

                    {/* NEW: retrieval toggle */}
                    <div className="flex items-center gap-2">
                      <input id="useRag" type="checkbox" checked={useRag} onChange={(e) => setUseRag(e.target.checked)} />
                      <label htmlFor="useRag" className="text-sm text-slate-700 dark:text-slate-200">Use retrieval (RAG)</label>
                    </div>

                    {/* NEW: evaluator chips + templates + descriptions */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Evaluators</label>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Selected: {selectedEvaluators.length}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(availableEvaluators).map(([id, meta]) => {
                          const checked = selectedEvaluators.includes(id);
                          const description = getEvaluatorDescription(id) || meta?.description || '';
                          const category = getEvaluatorCategory(id);
                          
                          return (
                            <div key={id} className="relative group">
                              <label className={`flex items-center gap-2 border rounded-full px-3 py-1 text-xs cursor-pointer ${checked ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-200' : 'bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'}`}>
                                <Checkbox checked={checked} onCheckedChange={(v) => {
                                  const isChecked = Boolean(v);
                                  setSelectedEvaluators((prev) => isChecked ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id));
                                }} />
                                <span>{meta?.title || id}</span>
                                {description && (
                                  <Info className="w-3 h-3 ml-1 text-slate-400" />
                                )}
                              </label>
                              
                              {/* Tooltip */}
                              {description && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 max-w-xs">
                                  <div className="font-medium text-emerald-300 mb-1">{category}</div>
                                  <div>{description}</div>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {Object.keys(availableEvaluators || {}).length === 0 && (
                          <div className="text-xs text-slate-500">No evaluators available.</div>
                        )}
                      </div>
                      {(Object.keys(availableEvaluators || {}).length > 0 && selectedEvaluators.length === 0) && (
                        <div className="text-xs text-rose-600 mt-2">Select at least one evaluator.</div>
                      )}

                      {Object.keys(availableEvaluators || {}).length > 0 && (
                        <details className="mt-3">
                          <summary className="text-xs text-slate-600 dark:text-slate-300 cursor-pointer flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            Evaluator details
                          </summary>
                          <div className="mt-2 space-y-2">
                            {Object.entries(availableEvaluators).map(([id, meta]) => {
                              const description = getEvaluatorDescription(id) || meta?.description || 'No description available.';
                              const category = getEvaluatorCategory(id);
                              
                              return (
                                <div key={id} className="text-xs p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold">{meta?.title || id} <span className="text-slate-500">({id})</span></div>
                                    <span className="px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs">
                                      {category}
                                    </span>
                                  </div>
                                  <div className="text-slate-600 dark:text-slate-300">{description}</div>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}

                      <div className="mt-4 border-t pt-3">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Run templates</div>
                        <div className="flex gap-2 mb-2">
                          <input className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700 flex-1" placeholder="Template name" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} />
                          <Button className="px-3 py-1.5 text-xs" variant="outline" onClick={() => {
                            const name = newTemplateName.trim();
                            if (!name) return;
                            const tpl = { id: `tpl_${Date.now()}`, name, useRag, evaluators: selectedEvaluators, concurrency };
                            setTemplates((prev) => [tpl, ...prev.filter(t => t.name !== name)]);
                            setNewTemplateName("");
                          }}>Save</Button>
                        </div>
                        {templates.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {templates.map((t) => (
                              <div key={t.id} className="flex items-center gap-2 border rounded px-2 py-1 text-xs bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700">
                                <span className="font-semibold">{t.name}</span>
                                <button className="underline" onClick={() => { setUseRag(Boolean(t.useRag)); setSelectedEvaluators(Array.isArray(t.evaluators) ? t.evaluators : []); setConcurrency(Math.max(1, Math.min(8, Number(t.concurrency) || 1))); }}>Apply</button>
                                <button className="text-rose-600" onClick={() => setTemplates((prev) => prev.filter(x => x.id !== t.id))}>Delete</button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500">No templates yet. Save your current options for reuse.</div>
                        )}
                      </div>
                    </div>

                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2" disabled={runButtonDisabled} onClick={handleStartRun}>
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      {loading ? "Starting…" : (activeView?.kind === 'selection' ? 'Run selection' : `Run ${matchingCases.length} test${matchingCases.length === 1 ? "" : "s"}`)}
                    </Button>
                    <p className="text-xs text-slate-500 dark:text-slate-400">This will queue a run locally. Next, we’ll integrate your evaluator script so this triggers real execution.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <div className="grid gap-4">
              {runs.length === 0 ? (
                <Card className="shadow-sm border dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
                  <CardContent className="p-8 text-center text-slate-600 dark:text-slate-300">No runs yet. Start one from the Run tab.</CardContent>
                </Card>
              ) : (
                runs.map((run) => (
                  <Card key={run.id} className="shadow-lg border dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 backdrop-blur-sm">
                    <CardHeader className="border-b border-slate-100 dark:border-slate-800">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg dark:text-slate-100">{run.notes || run.id}</CardTitle>
                        <div className="flex items-center gap-2">{getStatusBadge(run.status)}</div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="grid md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-slate-500 dark:text-slate-400">Created</div>
                          <div className="text-slate-800 dark:text-slate-200">{formatDate(run.created_at)}</div>
                        </div>
                        <div>
                          <div className="text-slate-500 dark:text-slate-400">Environment</div>
                          <div className="text-slate-800 dark:text-slate-200">{run.environment}</div>
                        </div>
                        <div>
                          <div className="text-slate-500 dark:text-slate-400">Concurrency</div>
                          <div className="text-slate-800 dark:text-slate-200">{run.concurrency}</div>
                        </div>
                        <div>
                          <div className="text-slate-500 dark:text-slate-400">Selected</div>
                          <div className="text-slate-800 dark:text-slate-200">{run.counts?.selected ?? 0}</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Filters</div>
                        <pre className="text-xs bg-slate-900 text-slate-200 p-3 rounded-lg overflow-auto max-h-48">{JSON.stringify(run.filters, null, 2)}</pre>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Backend tab moved to its own page */}
        </Tabs>
      </div>

  {/* Toasts are non-blocking; no modal */}
    </div>
  );
}


function useInterval(callback, delayMs) {
  useEffect(() => {
    if (delayMs == null) return;
    const id = setInterval(callback, delayMs);
    return () => clearInterval(id);
  }, [callback, delayMs]);
}

function BackendRunsTab() {
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadRuns = useCallback(async () => {
    try {
      setError("");
      const list = await listEvalRuns(50);
      setRuns(list);
      if (!selectedId && list.length) setSelectedId(list[0].id);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [selectedId]);

  const loadSelected = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getEvalRun(id);
      setSelected(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    loadSelected(selectedId);
  }, [selectedId]);

  // Auto-poll while running/queued
  useInterval(() => {
    const st = selected?.status;
    if (selectedId && (st === 'queued' || st === 'started' || st === 'running')) {
      loadSelected(selectedId);
      loadRuns();
    }
  }, 2000);

  const statusBadge = (status) => {
    const base = "px-2 py-0.5 rounded text-xs";
    const map = {
      queued: "bg-amber-100 text-amber-800",
      started: "bg-blue-100 text-blue-800",
      running: "bg-blue-100 text-blue-800",
      success: "bg-emerald-100 text-emerald-800",
      failed: "bg-rose-100 text-rose-800",
    };
    return <span className={`${base} ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
  };

  const metricRows = () => {
    const m = selected?.results?.metrics || selected?.metrics;
    if (!m) return null;
    return Object.entries(m).map(([k, v]) => (
      <div key={k} className="flex justify-between text-sm border-b py-1">
        <div className="text-slate-600">{k}</div>
        <div className="text-slate-800 font-semibold">{String(v)}</div>
      </div>
    ));
  };

  const rowsTable = () => {
    const rows = selected?.results?.rows || selected?.rows || [];
    if (!rows?.length) return <div className="text-sm text-slate-600">No row-level results.</div>;
    return (
      <div className="space-y-3">
        {rows.map((r, idx) => (
          <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-white">
            <div className="text-xs text-slate-500 mb-1">Line {r.line_number ?? idx}</div>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-slate-500">Query</div>
                <div className="text-slate-800 break-words">{r["inputs.query"] || r.inputs?.query || ''}</div>
              </div>
              <div>
                <div className="text-slate-500">Response</div>
                <div className="text-slate-800 break-words">{r["inputs.response"] || r.inputs?.response || ''}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-slate-500">Context</div>
                <div className="text-slate-800 break-words text-xs">{r["inputs.context"] || r.inputs?.context || ''}</div>
              </div>
            </div>
            <div className="mt-2 grid md:grid-cols-3 gap-2 text-xs">
              {Object.entries(r).filter(([k]) => k.startsWith('outputs.')).map(([k, v]) => (
                <div key={k} className="flex justify-between border rounded p-2">
                  <div className="text-slate-600 truncate" title={k}>{k}</div>
                  <div className="text-slate-800 font-semibold ml-2">{String(v)}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-1 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-700">Recent Runs</div>
          <button className="text-xs text-emerald-700" onClick={loadRuns}>Refresh</button>
        </div>
        <div className="space-y-2">
          {runs.map((r) => (
            <div key={r.id} onClick={() => setSelectedId(r.id)} className={`p-3 rounded-lg border cursor-pointer ${selectedId === r.id ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800 truncate mr-2">{r.id}</div>
                {statusBadge(r.status)}
              </div>
              <div className="text-xs text-slate-600 mt-1">{r.updated_at || r.created_at}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        {!selectedId ? (
          <div className="text-sm text-slate-600">Select a run to view details.</div>
        ) : loading ? (
          <div className="text-sm text-slate-600">Loading…</div>
        ) : error ? (
          <div className="text-sm text-rose-700">{error}</div>
        ) : !selected ? (
          <div className="text-sm text-slate-600">No data.</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-slate-800">Run {selected.id}</div>
              {statusBadge(selected.status)}
            </div>
            {selected.error && (
              <div className="text-sm text-rose-700">{selected.error}</div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 rounded-lg border bg-white">
                <div className="text-sm font-medium text-slate-700 mb-2">Metrics</div>
                {metricRows() || <div className="text-sm text-slate-600">No metrics available.</div>}
              </div>
              <div className="p-3 rounded-lg border bg-white">
                <div className="text-sm font-medium text-slate-700 mb-2">Options</div>
                <pre className="text-xs bg-slate-900 text-slate-200 p-2 rounded max-h-64 overflow-auto">{JSON.stringify(selected.options || {}, null, 2)}</pre>
              </div>
            </div>
            <div className="p-3 rounded-lg border bg-white">
              <div className="text-sm font-medium text-slate-700 mb-2">Rows</div>
              {rowsTable()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}